
import { Timeline } from "gsap";
import { SanGameConfig } from "../../../SanGameConfig";
import { SlotGame } from "@nolimitcity/slot-game/bin/core/SlotGame";
import { WinCounter } from "../../independent/countup/WinCounter";
import { GameEvent } from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import { EventHandler } from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import { IWinRatioConfig } from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import { SpinEvent } from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import { StateHandler } from "@nolimitcity/slot-game/bin/core/utils/StateHandler";
import { Presentation, PresentationState } from "@nolimitcity/slot-game/bin/core/gamecontroller/presentations/Presentation";
import { SanParsedGameData } from "../../../server/data/SanParsedGameData";
import { PosterManager, BigWinPosterState } from "../../independent/poster/PosterManager";
import { SanEngineManager } from "../../../engine/SanEngineManager";
import { NormalWinPresentation } from "../normalwin/NormalWinPresentation";
import { ParticlesManager } from "../../independent/particles/ParticlesManager";
import { RevealerManager } from "../../independent/revealer/RevealerManager";
import { Revealer } from "../../independent/revealer/Revealer";
import { SanBackground } from "../../background/SanBackground";
import { SoundConfig } from "../../../SoundConfig";
import { SoundManager } from "../../../resource/sound/SoundManager";
import {SanGameModuleConfig} from "../../../SanGameModuleConfig";

class BigWinStates extends StateHandler{
    public static readonly START:string = "start";
    public static readonly SHOW:string = "show";
    public static readonly STOP:string = "stop";
    public static readonly DONE:string = "done";
}

export class BigWinPresentation extends Presentation {

    private _winRatios:IWinRatioConfig[];
    private _gameData:SanParsedGameData;

    private _winLevel:number = 0;
    private _currLevel:number = 0;

    private _bounceDuration:number = 0.3 * 1.5;
    private _bigWinStates:BigWinStates = new BigWinStates();

    private bankBroken:boolean = false;

    private preWinSkip:boolean = false;

    public initialization(){
        this._winRatios = SanGameConfig.instance.WIN_RATIOS.bigWin;
    }

    public setParsedGamedData(data:SanParsedGameData):void {
        this._winLevel = 0;
        this._currLevel = 0;

        this._gameData = data;
        this._bigWinStates.set(BigWinStates.START);

        this.bankBroken = data.rawData.brokeTheBank;

        this._winLevel = this._winRatios.findIndex((indx) => indx.ratio == data.currentWinRatioConfig?.ratio);

        if( this._winLevel == 0 && this.betValueRatio(this._winLevel) == this.nextValueRatio(this._winLevel)){
            this.PresentationState.set(PresentationState.INACTIVE);
        }
    }
  
    public checkForActivation(data:SanParsedGameData):boolean{ return data.isRealBigwin; }

    public getPresentation():void {
        switch (this._bigWinStates.state) {
            case BigWinStates.START:
                this.preWin();
                break;
            case BigWinStates.SHOW:
                this.preWinSkip = true;
                EventHandler.dispatchEvent(new GameEvent(SpinEvent.WANT_SKIPPABLE));
                this.playBigWinLevelAnimation();
                break;
            case BigWinStates.STOP:
                this.postWin();
                break;
            case BigWinStates.DONE:
                this.PresentationState.set(PresentationState.INACTIVE);
                break;
            default:
                break;
        }
    }

    private preWin():void{
        this._bigWinStates.set(BigWinStates.SHOW);
        this.preWinSkip = false;
        SoundManager.playSoundEffect(SoundConfig.instance.BIG_WIN_INIT);
        SoundManager.fadeForBigWin(0,1064);
        const fadeDelay:number = 0.6;
        const glowDurr:number = 2.5;

        this.pTimeline.fromTo(WinCounter.normalCounter,fadeDelay/2,{alpha:1},{alpha:0});
        this.pTimeline.add(() => SanBackground.instance.playAttentionBlackFlash(fadeDelay*2,0.5,0),0);
        
        for (let reel = 0; reel < this._gameData.allWinData.reelWinPositions.length; reel++) {
            let posRow: boolean[] = this._gameData.allWinData.reelWinPositions[reel];
            for (let row = 0; row < posRow.length; row++) {
                if(!posRow[row]) continue;
                const lvl:number = 3;
                if (row == 0 || row == posRow.length - 1){
                    const bar: Revealer = row == 0 ? RevealerManager.getTopBar(reel) : RevealerManager.getBotBar(reel);
                    this.pTimeline.add(() => bar.glowBiggy(glowDurr,lvl),reel*0.1)
                }
                else
                    this.pTimeline.add(() => SanEngineManager.getReel(reel).getSymbol(row - 1)!.glowBiggy(glowDurr,lvl), reel*0.1);
            }
        }

        this.pTimeline.add(() => {
            PosterManager.instance.play("bigWin",undefined,{config:{
                animationName:BigWinPosterState.bigWinTypeLvl(0),
                loop:true
            }});
            ParticlesManager.playBigwin(0);
            PosterManager.instance.setInteractive(true);
        },1.064);

        this.pTimeline.add(() => { WinCounter.normalCounter.isBigWinPos = true; WinCounter.normalCounter.onResize()})
        this.pTimeline.to(WinCounter.normalCounter,0.05,{alpha:1});
    }

    private isDoubleExpWin(reelPos:number ,row:boolean[]):boolean{
        const revExpanded:boolean = RevealerManager.getTopBar(reelPos).isExpanded &&  RevealerManager.getBotBar(reelPos).isExpanded;
        const hits:boolean = row[0] && row[row.length-1];

        return revExpanded && hits;
    }
    
    private postWin():void {

        SoundManager.stopSoundEffect(SoundConfig.instance.BIG_WIN_LVL + (Math.min(this._winLevel,4) + 1));
        SoundManager.playSoundEffect(SoundConfig.instance.BIG_WIN_ENDS);
        
        PosterManager.instance.setInteractive(false);
        PosterManager.instance.stop();
        ParticlesManager.stopBigWin();
        
        let animDuration:number = 0.6;
        let aborDelay:number = 0.2;

        if(this.betValueRatio(this._winLevel) != this.nextValueRatio(this._winLevel)){
            this.pTimeline.add([
                // () => WinCounter.normalCounter.showCounter(this._gameData!.singleWin, undefined,true),
                () => NormalWinPresentation.winAnim.winLvlAnimation(3),
                () => WinCounter.normalCounter.bounceHit(3,undefined,true),
                () => ParticlesManager.normalParticles.enableEmit(true),
            ],"-="+0.25);
            this.pTimeline.to(this,0.25,{});
        }

        this.pTimeline.add(()=>  WinCounter.normalCounter.abort(this._gameData!.singleWin, undefined, 1.5));
        this.pTimeline.add(()=>  WinCounter.normalCounter.showWaysWon(this._gameData.allWinData.totalWinCombination, 0.85,0.6),1.2);

        let reelDelay:number = 0.15;

        this.pTimeline.to(this,1.25,{});
        const delPos:number =  this.pTimeline.totalDuration() - 0.6;

        for (let reel = 0; reel < this._gameData.allWinData.reelWinPositions.length; reel++) {
            let posRow: boolean[] = this._gameData.allWinData.reelWinPositions[reel];
            const startPos:number = aborDelay+reel*reelDelay + delPos;
            for (let row = 0; row < posRow.length; row++) {
                if(!posRow[row]) continue;
                const lvl:number = this._gameData.allWinData.biggestWinLvl;
                if (row == 0 || row == posRow.length - 1){
                    const bar: Revealer = row == 0 ? RevealerManager.getTopBar(reel) : RevealerManager.getBotBar(reel);
                    this.pTimeline.add(() => bar.glow(animDuration,lvl),startPos)
                }
                else
                    this.pTimeline.add(() => SanEngineManager.getReel(reel).getSymbol(row - 1)!.glow(animDuration,lvl), startPos);
            }
        }
       
        this.pTimeline.to(this,animDuration,{});

        if(!this.bankBroken)
            SoundManager.endForBigWin(1,1000);

        this._bigWinStates.set(BigWinStates.DONE);
    }

    public playBigWinLevelAnimation():void {
        if(this._currLevel >= this._winLevel){
            const isTotalWin:boolean = this._gameData!.totalWin != this._gameData.singleWin;
            let countUpDurr:number = this._winRatios[this._winLevel].duration;
            const beginDurr:number = this._currLevel == 0 ?  0 : this._bounceDuration;

            if(this.betValueRatio(this._winLevel) != this.nextValueRatio(this._winLevel))
                this.pTimeline.add(this.playBigWinCountUp(this._winLevel, countUpDurr),beginDurr);

            if(this._currLevel > 0 && this._currLevel <= 4){
                ParticlesManager.playBigwin(this._currLevel)
                this.pTimeline.add(() => PosterManager.instance.play("bigWin",1,{config:{
                    animationName:BigWinPosterState.bigWinTypeLvl(this._currLevel),
                    loop:true
                }}),0);
            }

            if(this._currLevel >= 4)  SoundManager.playSoundEffect(SoundConfig.instance.BIG_WIN_EXPLODE);

            this.pTimeline.add(() => SlotGame.keypad.setWin(this._gameData!.totalWin, isTotalWin, SanGameModuleConfig.featurePrice.isWinBelowStake()));
            this._bigWinStates.set(BigWinStates.STOP);
        }else{
            const presLvl:number = this._currLevel;

            const beginDurr:number = presLvl == 0 ?  0 : this._bounceDuration;

            let countUpDurr:number = this._winRatios[presLvl].duration;
            this.pTimeline.to(this,beginDurr,{},0);

            this.pTimeline.add(() => this.playBigWinCountUp(presLvl, countUpDurr),beginDurr);

            if(presLvl > 0 && this._winLevel !== presLvl && presLvl <= 4){
                ParticlesManager.playBigwin(presLvl)
                this.pTimeline.add(() => PosterManager.instance.play("bigWin",1,{config:{
                    animationName:BigWinPosterState.bigWinTypeLvl(presLvl),
                    loop:true
                }}),0);
            }
            if(presLvl >= 4)  SoundManager.playSoundEffect(SoundConfig.instance.BIG_WIN_EXPLODE);

            this.pTimeline.add(
                [
                    () => WinCounter.normalCounter.stopCounter(this.nextValueRatio(presLvl)),
                    () => NormalWinPresentation.winAnim.winLvlManiacAnimation(3),
                    
                    () => WinCounter.normalCounter.bounceHit(presLvl+3,undefined,true),
                    () => ParticlesManager.normalParticles.enableEmit(true)
                ]
            ,countUpDurr+beginDurr);

            this.pTimeline.to(this,countUpDurr+beginDurr,{},0);
            this._currLevel++;
        }
    }

    protected playBigWinCountUp(level:number, duration:number):Timeline | null {
        const fromValue:number = this.betValueRatio(level);
        const toValue:number = this.nextValueRatio(level);

        return WinCounter.normalCounter.getCountUpAnimation(fromValue, toValue, duration, level+2,true);
    }

    public stopPresentation():void {
        
        SanEngineManager.RSWindow((reel:number,row:number) =>{
            SanEngineManager.getReel(reel).getSymbol(row)?.attentionDarkness(0,0.25);
        })

        RevealerManager.getAllBars().forEach(b => b.attentionDarkness(0,0.25));
    }

    public skipPresentation():void{
        if(!this.preWinSkip) return;
        
        if(this._bigWinStates.equal(BigWinStates.SHOW) || this._bigWinStates.equal(BigWinStates.STOP)){
            SoundManager.stopSoundEffect(SoundConfig.instance.BIG_WIN_INIT);
            SoundManager.stopSoundEffect(SoundConfig.instance.BIG_WIN_INIT_COUNTUP);
            this.pTimeline!.totalProgress(1); 
        }
    }

    private betValueRatio(level:number):number{
        return this._gameData!.playedBetValue * this._winRatios[level].ratio;
    }

    private nextValueRatio(level:number):number{
        return level + 1 > this._winLevel ? this._gameData!.singleWin : this.betValueRatio(level + 1);
    }
}